import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface ExecutionImpact {
  strengths: string[];
  challenges: string[];
}

export interface WorkplaceQuality {
  title: string;
  match: boolean;
  score: number;
  score_range: string;
  definition: string;
  key_traits: string[];
  // v2 score-band narrative fields (shared field names with work motivators,
  // surfaced under workplace-quality-specific headings in the report).
  what_this_tells_you?: string;
  in_the_role?: string;
  watch_out_for?: string;
  works_best_in?: string;
  work_manifestation: string[];
  execution_impact: ExecutionImpact;
  behaviours: string[];
  characteristics: string[];
}

export interface WorkplaceQualitiesData {
  score: number;
  qualities: WorkplaceQuality[];
  employer_expectation: WorkplaceQuality[];
  version: number;
}

interface WorkplaceQualitiesResponse {
  message: string;
  data: WorkplaceQualitiesData;
}

/**
 * Hook for fetching workplace qualities data
 */
export const useWorkplaceQualities = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workplaceQualities', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<WorkplaceQualitiesResponse>(
        `/reports/${reportId}/workplace-qualities/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch workplace qualities';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    workplaceQualities: data,
    isLoading,
    error,
    refetch,
  };
};
